import React, { useState, useEffect } from "react";
import {
  MapPin, Plus, Pencil, Trash2, Loader2, Star, Home, Building2, Warehouse, X, Check
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import StateProvinceSelect from "../../components/StateProvinceSelect";
import { formatPostal } from "../../utils/addressUtils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("portal_token")}` } });

const LABEL_OPTIONS = [
  { value: "Home", icon: Home, color: "#3B82F6" },
  { value: "Office", icon: Building2, color: "#8B5CF6" },
  { value: "Warehouse", icon: Warehouse, color: "#F59E0B" },
  { value: "Other", icon: MapPin, color: "#6B7280" },
];

const getLabelIcon = (label) => {
  const opt = LABEL_OPTIONS.find(o => o.value.toLowerCase() === label?.toLowerCase());
  return opt || LABEL_OPTIONS[3];
};

// ======================== ADDRESS FORM DIALOG ========================

const AddressFormDialog = ({ open, onClose, address, onSaved }) => {
  const [form, setForm] = useState({
    label: "Home", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", notes: "", is_default: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (address) {
        setForm({
          label: address.label || "Home",
          address_line1: address.address_line1 || "",
          address_line2: address.address_line2 || "",
          city: address.city || "",
          state: address.state || "",
          zip_code: address.zip_code || "",
          notes: address.notes || "",
          is_default: address.is_default || false,
        });
      } else {
        setForm({ label: "Home", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", notes: "", is_default: false });
      }
    }
  }, [open, address]);

  const handleSave = async () => {
    if (!form.address_line1.trim() || !form.city.trim() || !form.state.trim() || !form.zip_code.trim()) {
      toast.error("Please fill in all required address fields");
      return;
    }
    setSaving(true);
    try {
      if (address?.address_id) {
        await axios.put(`${API}/portal/addresses/${address.address_id}`, form, getConfig());
        toast.success("Address updated");
      } else {
        await axios.post(`${API}/portal/addresses`, form, getConfig());
        toast.success("Address added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[440px] p-0 gap-0 rounded-2xl bg-white border-0 shadow-2xl" data-testid="address-form-modal">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-lg font-semibold text-slate-800 font-['Outfit']">
            {address ? "Edit Address" : "Add New Address"}
          </DialogTitle>
        </DialogHeader>

        <div className="p-5 pt-0 space-y-4">
          {/* Label selector */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-500">Label</Label>
            <div className="flex gap-2">
              {LABEL_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, label: opt.value }))}
                  data-testid={`addr-label-${opt.value.toLowerCase()}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                    form.label === opt.value
                      ? "shadow-sm"
                      : "border-slate-100 text-slate-500 hover:border-slate-200"
                  }`}
                  style={form.label === opt.value ? { color: opt.color, backgroundColor: `${opt.color}08`, borderColor: `${opt.color}30` } : {}}
                >
                  <opt.icon className="w-3.5 h-3.5" />
                  {opt.value}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-500">Address Line 1 *</Label>
            <Input value={form.address_line1} onChange={e => setForm(f => ({ ...f, address_line1: e.target.value }))}
              placeholder="123 Main Street" className="rounded-xl border-slate-100 text-sm" data-testid="addr-line1" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-500">Address Line 2</Label>
            <Input value={form.address_line2} onChange={e => setForm(f => ({ ...f, address_line2: e.target.value }))}
              placeholder="Apt, Suite, Unit (optional)" className="rounded-xl border-slate-100 text-sm" data-testid="addr-line2" />
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">City *</Label>
              <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                placeholder="Sacramento" className="rounded-xl border-slate-100 text-sm" data-testid="addr-city" />
            </div>
            <div className="col-span-1 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">State / Province *</Label>
              <StateProvinceSelect value={form.state} onChange={(v) => setForm(f => ({ ...f, state: v }))}
                className="rounded-xl border-slate-200 text-sm bg-white text-slate-900" data-testid="addr-state" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">ZIP / Postal *</Label>
              <Input value={form.zip_code} onChange={e => setForm(f => ({ ...f, zip_code: formatPostal(e.target.value) }))}
                placeholder="95834 or A1A 1A1" className="rounded-xl border-slate-100 text-sm" data-testid="addr-zip" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-500">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Gate code, access instructions..." className="rounded-xl border-slate-100 text-sm min-h-[60px] resize-none" data-testid="addr-notes" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="addr-default-toggle">
            <button type="button" onClick={() => setForm(f => ({ ...f, is_default: !f.is_default }))}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                form.is_default ? "bg-[#0046BE] border-[#0046BE]" : "border-slate-200"
              }`}>
              {form.is_default && <Check className="w-3 h-3 text-white" />}
            </button>
            <span className="text-sm text-slate-600">Set as default address</span>
          </label>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => onClose(false)} className="flex-1 rounded-xl border-slate-100 text-slate-500" data-testid="addr-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}
              className="flex-[2] bg-[#0046BE] hover:bg-[#0036A0] text-white rounded-xl" data-testid="addr-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (address ? "Update Address" : "Add Address")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ======================== MAIN ADDRESSES PAGE ========================

const PortalAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/portal/addresses`, getConfig());
      setAddresses(res.data.addresses || []);
    } catch (err) {
      console.error("Error fetching addresses:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAddresses(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/portal/addresses/${deleteTarget}`, getConfig());
      toast.success("Address deleted");
      fetchAddresses();
    } catch (err) {
      toast.error("Failed to delete address");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEdit = (addr) => { setEditingAddress(addr); setFormOpen(true); };
  const openAdd = () => { setEditingAddress(null); setFormOpen(true); };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0046BE]" /></div>;
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="portal-addresses">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Outfit']">My Addresses</h1>
        <Button onClick={openAdd} className="bg-[#0046BE] hover:bg-[#0036A0] text-white rounded-xl text-sm h-9 px-4" data-testid="add-address-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Address
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
          <MapPin className="w-10 h-10 mx-auto text-slate-200 mb-3" />
          <p className="text-slate-400 text-sm font-medium">No saved addresses</p>
          <p className="text-xs text-slate-300 mt-1 mb-4">Add addresses for faster service booking</p>
          <Button onClick={openAdd} variant="outline" className="rounded-xl border-slate-200 text-[#0046BE] text-sm" data-testid="add-first-address-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Your First Address
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {addresses.map((addr) => {
            const labelOpt = getLabelIcon(addr.label);
            return (
              <div key={addr.address_id}
                className="bg-white rounded-2xl p-5 relative group transition-all duration-200 hover:translate-y-[-2px]"
                style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}
                data-testid={`address-card-${addr.address_id}`}
              >
                {/* Default badge */}
                {addr.is_default && (
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100">
                      <Star className="w-2.5 h-2.5 fill-current" /> Default
                    </span>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${labelOpt.color}08` }}>
                    <labelOpt.icon className="w-5 h-5" style={{ color: labelOpt.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-700 text-sm font-['Outfit']">{addr.label}</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{addr.address_line1}</p>
                    {addr.address_line2 && <p className="text-xs text-slate-400">{addr.address_line2}</p>}
                    <p className="text-xs text-slate-400">{addr.city}, {addr.state} {addr.zip_code}</p>
                    {addr.notes && (
                      <p className="text-[11px] text-slate-300 mt-2 italic">"{addr.notes}"</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1.5 mt-4 pt-3" style={{ borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(addr)}
                    className="flex-1 text-xs text-slate-400 hover:text-[#0046BE] hover:bg-[#0046BE]/5 rounded-lg h-8"
                    data-testid={`edit-address-${addr.address_id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(addr.address_id)}
                    className="flex-1 text-xs text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg h-8"
                    data-testid={`delete-address-${addr.address_id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Address Form Modal */}
      <AddressFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        address={editingAddress}
        onSaved={fetchAddresses}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Address</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to remove this address? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-xl" data-testid="confirm-delete-address">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalAddresses;
